import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, BarChart3, Globe, Gift, FileText } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const cardIcons = [MapPin, BarChart3, Globe, Gift, FileText];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: 'easeOut', delay },
});

export default function MultiLocation() {
  const { t } = useLanguage();
  const ml = t.multiLocation;

  return (
    <section id="multi-sede" className="py-14 md:py-20 bg-[#1F2833]" data-testid="multi-location-section">
      <div className="section-container">
        <motion.div className="text-center mb-6" {...fadeUp()}>
          <div className="divider-gold mx-auto" />
          <h2 className="heading-lg mb-2">
            {ml.title}<br />
            <span className="text-gradient-gold">{ml.titleHighlight}</span>
          </h2>
          <p className="text-[#8A9099] text-base max-w-2xl mx-auto mt-4">{ml.body}</p>
        </motion.div>

        {/* Visual map placeholder */}
        <motion.div className="relative mx-auto max-w-2xl mb-14" {...fadeUp(0.1)}>
          <div className="bg-[#171E26] rounded-2xl border border-[#0F6B49]/30 p-8 overflow-hidden">
            {/* Simulated map */}
            <div className="relative h-48 bg-[#1F2833] rounded-xl overflow-hidden">
              <div className="absolute inset-0 opacity-20"
                style={{backgroundImage: 'radial-gradient(circle at 20% 30%, #0F6B49 0%, transparent 40%), radial-gradient(circle at 70% 60%, #0F6B49 0%, transparent 35%), radial-gradient(circle at 50% 80%, #0F6B49 0%, transparent 30%)'}} />
              {/* Location dots */}
              {[[20,30],[45,55],[70,25],[35,70],[65,75],[80,45]].map(([x, y], i) => (
                <div key={i} className="absolute" style={{left:`${x}%`,top:`${y}%`}}>
                  <div className="relative">
                    <div className="w-3 h-3 rounded-full bg-[#D5A63B] border-2 border-[#D5A63B]/50 shadow-[0_0_8px_rgba(213,166,59,0.6)]" />
                    <div className="absolute -inset-1 rounded-full bg-[#D5A63B]/20 animate-ping" />
                  </div>
                </div>
              ))}
              {/* Center hub */}
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                <div className="w-8 h-8 rounded-full bg-[#0F6B49] border-2 border-[#D5A63B] shadow-[0_0_16px_rgba(15,107,73,0.6)]
                  flex items-center justify-center">
                  <span className="text-white text-[8px] font-black font-exo">MP</span>
                </div>
              </div>
            </div>
            <div className="text-center mt-4">
              <span className="text-[#8A9099] text-xs">{ml.support}</span>
            </div>
          </div>
        </motion.div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {ml.cards.map((card, i) => {
            const Icon = cardIcons[i];
            return (
              <motion.div key={i} className="card-dark p-5 text-center" {...fadeUp(0.15 + i * 0.07)}
                data-testid={`multi-location-card-${i}`}>
                <Icon size={20} className="text-[#D5A63B] mx-auto mb-3" />
                <h3 className="font-exo font-bold italic text-[#F3F1EB] text-sm mb-1.5">{card.title}</h3>
                <p className="text-[#8A9099] text-xs leading-relaxed">{card.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
